import React, { useEffect, useMemo, useState } from 'react';
import { Rechnungsvorschau, VorgemerkteRechnung } from '../types';
import rechnungService from '../services/rechnungService';
import { Icon, Spinner } from './ui';
import { fmtDatum } from '../config/rechnungStatus';
import { formatBetrag } from '../utils/preis';

export type Zeitraum = 'monat' | '30' | '90';

// Als Liste, nicht per Object.keys: Ziffern-Schlüssel ('30', '90') sortiert JS immer nach vorn.
const ZEITRAEUME: Zeitraum[] = ['monat', '30', '90'];

const ZEITRAUM_LABEL: Record<Zeitraum, string> = {
  monat: 'Monatsende',
  '30': '30 Tage',
  '90': '90 Tage',
};

const VORSCHAU_KEY = 'rechnungen.vorschau';

/** Ob die Vorschau an ist, merkt sich der Browser – localStorage kann fehlen oder werfen. */
export const vorschauGemerkt = (): boolean => {
  try { return localStorage.getItem(VORSCHAU_KEY) === '1'; } catch { return false; }
};
export const vorschauMerken = (an: boolean) => {
  try { localStorage.setItem(VORSCHAU_KEY, an ? '1' : '0'); } catch { /* egal */ }
};

/** ISO-Datum in lokaler Zeit – `toISOString` würde nach UTC verschieben. */
const isoLokal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const bisDatum = (z: Zeitraum): string => {
  const d = new Date();
  if (z === 'monat') return isoLokal(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  d.setDate(d.getDate() + Number(z));
  return isoLokal(d);
};

/**
 * Vorgemerkte Rechnungen – was der tägliche Lauf in den nächsten Tagen rein rechnerisch anlegen
 * wird, wie die vorgemerkten Buchungen auf dem Girokonto. Nichts davon existiert schon: kein
 * Entwurf, keine Nummer. Dargestellt werden sie in der [RechnungsZeitachse] über „Heute“ – hohl,
 * blass und ohne Status-Aktionen; sie führen zum Vertrag statt zu einer Rechnung.
 */
export const useVorgemerkteRechnungen = (aktiv: boolean, zeitraum: Zeitraum, kundeId?: number) => {
  const [vorschau, setVorschau] = useState<Rechnungsvorschau | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setVorschau(null);
    setFehler(null);
    if (!aktiv) return;
    let abgebrochen = false;
    rechnungService.vorschau({ bis: bisDatum(zeitraum), kundeId })
      .then(v => { if (!abgebrochen) setVorschau(v); })
      .catch(e => { if (!abgebrochen) setFehler(e?.response?.data?.error || 'Vorschau konnte nicht geladen werden'); });
    return () => { abgebrochen = true; };
  }, [aktiv, zeitraum, kundeId]);

  // Die Liste ist „neueste zuerst“ – der späteste Jahrestag steht also ganz oben, der nächste direkt
  // über den echten Rechnungen.
  const rechnungen = useMemo(
    () => [...(vorschau?.rechnungen ?? [])].sort((a, b) => b.erstelltAm.localeCompare(a.erstelltAm)),
    [vorschau],
  );
  return { vorschau, rechnungen, fehler, laedt: aktiv && !vorschau && !fehler };
};

/**
 * Schalter „Vorgemerkte“ im iOS-Stil – auf dem iPad besser zu treffen als eine Checkbox. Merkt sich
 * seinen Zustand im Browser.
 */
export const VorschauSchalter: React.FC<{
  aktiv: boolean; onAktiv: (an: boolean) => void; label?: string;
}> = ({ aktiv, onAktiv, label = 'Vorgemerkte' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={aktiv}
    onClick={() => { onAktiv(!aktiv); vorschauMerken(!aktiv); }}
    className="flex min-h-[40px] shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg pr-1 text-sm font-medium text-gray-700"
  >
    <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
      aktiv ? 'bg-brand-600' : 'bg-gray-300'}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
        aktiv ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
    </span>
    <Icon.Clock className="h-4 w-4 text-gray-400" />
    {label}
  </button>
);

/** Zeitraum der Vorschau als Segment-Leiste – drei feste Werte, ein Tipp statt Dropdown. */
export const ZeitraumAuswahl: React.FC<{ zeitraum: Zeitraum; onZeitraum: (z: Zeitraum) => void }> =
  ({ zeitraum, onZeitraum }) => (
    <div role="radiogroup" aria-label="Zeitraum der Vorschau" className="inline-flex shrink-0 rounded-lg bg-gray-100 p-0.5">
      {ZEITRAEUME.map(z => (
        <button
          key={z}
          type="button"
          role="radio"
          aria-checked={zeitraum === z}
          onClick={() => onZeitraum(z)}
          className={`min-h-[36px] whitespace-nowrap rounded-md px-3 text-sm transition ${
            zeitraum === z ? 'bg-white font-medium text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          {ZEITRAUM_LABEL[z]}
        </button>
      ))}
    </div>
  );

/**
 * Kopf über den vorgemerkten Einträgen: bis wann gerechnet wurde und was zusammenkommt – oder warum
 * nichts kommt. Die Summe steht rechtsbündig in der Betragsspalte, wie ein Zwischensaldo.
 */
export const VorschauKopf: React.FC<{
  daten: ReturnType<typeof useVorgemerkteRechnungen>;
  /** Die tatsächlich angezeigten (ggf. gefilterten) Einträge. */
  liste?: VorgemerkteRechnung[];
}> = ({ daten: { vorschau, rechnungen, fehler, laedt }, liste = rechnungen }) => {
  const summe = liste.reduce((s, r) => s + r.summeBrutto, 0);
  let text: React.ReactNode;
  if (fehler) text = <span className="text-red-700">{fehler}</span>;
  else if (laedt || !vorschau) text = <span className="flex items-center gap-2"><Spinner className="h-4 w-4" /> Vorgemerkte werden berechnet</span>;
  else if (vorschau.hinweis) text = <span className="text-amber-800">{vorschau.hinweis}</span>;
  else if (liste.length === 0) text = <>Bis {fmtDatum(vorschau.bis)} entstehen keine weiteren Rechnungen.</>;
  else text = <>{liste.length === 1 ? 'Eine Rechnung' : `${liste.length} Rechnungen`} vorgemerkt bis {fmtDatum(vorschau.bis)}</>;

  return (
    // Bündig mit dem Karteninhalt der Zeitachse (Tag + Achse + Abstände + Kartenpolster = 6rem).
    <div className="flex items-baseline justify-between gap-3 py-2 pl-24 pr-4 text-sm text-gray-500">
      {text}
      {liste.length > 0 && !vorschau?.hinweis && (
        <span className="whitespace-nowrap font-semibold tabular-nums text-gray-500">{formatBetrag(summe)}</span>
      )}
    </div>
  );
};
